(function(){
'use strict';

CartModelFactory.$inject = ["$http", "$q", "$rootScope", "$window", "$timeout", "ipCookie", "scBanner", "scCampaignsService", "scAnalytics", "scCurrencyService", "scFeeOnTopService", "SC_CART_COOKIE", "recaptchaService", "scOrganizationsService"];
var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _asyncToGenerator(fn) { return function () { var gen = fn.apply(this, arguments); return new Promise(function (resolve, reject) { function step(key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { return Promise.resolve(value).then(function (value) { step("next", value); }, function (err) { step("throw", err); }); } } return step("next"); }); }; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function CartModelFactory($http, $q, $rootScope, $window, $timeout, ipCookie, scBanner, scCampaignsService, scAnalytics, scCurrencyService, scFeeOnTopService, SC_CART_COOKIE, recaptchaService, scOrganizationsService) {
  var WARN_AT = 1000 * 60;

  var EDITABLE_PROPS = ['capabilities', 'company_name', 'donation_amount', 'donation_frequency', 'donation_fee_on_top', 'donation_applied_fot_percent', 'donation_answers', 'donation_currency_code', 'team_id', 'team_lead_id', 'team_name', 'team_description', 'team_goal', 'team_answers', 'fixed_fot_enabled', 'registration_applied_fot_percent', 'donation_donor_covered_fee', 'donation_application_fee_percent', 'donation_processor_fee_percent', 'donation_raw_processor_fee_flat', 'donation_flex_rate_percent', 'donation_raw_flex_rate_amount', 'donation_raw_application_fee_amount', 'donation_raw_processor_fee_amount', 'registration_donor_covered_fee', 'registration_application_fee_percent', 'registration_processor_fee_percent', 'registration_raw_processor_fee_flat', 'registration_flex_rate_percent', 'registration_raw_flex_rate_amount', 'registration_raw_application_fee_amount', 'registration_raw_processor_fee_amount', 'registrant_opted_into_payment_sync'];

  // private with timeout $http drop-ins.
  // Note: not using these for all cart requests - notably deletes and finalizing payment
  function requestWithSoftTimeout(method, url, data, shortDurationMs, longDurationMs) {
    var enableLogging = scOrganizationsService.getTag('log-slow-rwf-cart');

    var config = {
      method: method,
      url: url
    };
    if (data) config.data = data; // don't include key if null/undefined

    var requestPromise = $http(config);

    if (!enableLogging) {
      return requestPromise;
    }

    var timeoutError = new Error('Request timed out');
    var shortTimeoutPromise = new Promise(function (_, reject) {
      return $timeout(function () {
        return reject(timeoutError);
      }, shortDurationMs);
    });
    var longTimeoutPromise = new Promise(function (_, reject) {
      return $timeout(function () {
        return reject(timeoutError);
      }, longDurationMs);
    });

    // catch the timeout only if the request is not yet complete
    Promise.race([requestPromise, shortTimeoutPromise]).catch(function (error) {
      if (error === timeoutError) {
        // log but fon't scare the user
        bugsnagClient.notify('Request is taking over ' + shortDurationMs + ' ms. Request: ' + method + ' ' + url);
      }
    });

    // catch the timeout only if the request is not yet complete
    Promise.race([requestPromise, longTimeoutPromise]).catch(function (error) {
      if (error === timeoutError) {
        // log and scare the user
        var msg = "We're having trouble processing your request due to network issues. Please check your internet connection.";
        scBanner(msg, 'error', false);
        bugsnagClient.notify('Request is taking over ' + longDurationMs + ' ms. Request: ' + method + ' ' + url);
      }
    });

    // callers still only have to worry about the main request
    return requestPromise;
  }

  // eslint-disable-next-line no-unused-vars
  function _getWithTimeout(url) {
    var durationMs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 12000;

    return requestWithSoftTimeout('GET', url, null, durationMs / 4, durationMs);
  }

  function postWithTimeout(url, data) {
    var durationMs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 12000;

    return requestWithSoftTimeout('POST', url, data, durationMs / 4, durationMs);
  }

  function putWithTimeout(url, data) {
    var durationMs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 12000;

    return requestWithSoftTimeout('PUT', url, data, durationMs / 4, durationMs);
  }

  // private to prevent people from using this method directly and accidentally saving only
  // partial state updates from the cart (which could cause changes like donation_amount or others
  // to be removed unintentionally)
  function updateCart(cartId, data) {
    return putWithTimeout('/frs-api/carts/' + cartId, data);
  }

  return function () {
    /* ---------------------------------------------------------------------- *
     * constructor()
     *
     * Sets the type and sets up the model.
     * ---------------------------------------------------------------------- */

    function CartModel(data, metadata) {
      var _this = this;

      _classCallCheck(this, CartModel);

      // Object type.
      this.type = 'cart';

      // Repository for item ids that will be converted to CartItems when the
      // cart is reserved.
      this.stagedItems = [];

      // An array whose indices hold the value of the last attendee # for the matching
      // staged cartItem. For example, this.attendeeIndices[3] will contain a number
      // representing the last attendee's index for stagedCartItems[3]. We use the
      // number of entries for each cart item to figure out the correct index for all
      // the other attendees in the cart item (if any).
      this.attendeeIndices = [];

      // Set up metadata, if provided.
      // NOTE: This was originally for team data, which is now represented
      // directly in cart properties. But I'm leaving it in case it's useful
      // in the future.
      this.metadata = _.defaults(metadata || {}, { status: 'incomplete' });

      this.callbacks = {
        warn: [],
        expire: []
      };
      this.warnTimer = null;
      this.expireTimer = null;
      this.timeLeft = WARN_AT;

      // Fee calculation initialization
      scFeeOnTopService.init();
      this.registrationFeeOnTop = _.get(scCampaignsService, 'active.current.ticket_pass_on_fees', false);

      // Set up model.
      this.syncModel(data);

      // Watch donation_amount and donation_fee_on_top for fee calculation.
      this.watchDonation();

      var processorName = _.has($window.SC.processors, 'Stripe') ? 'STRIPE' : _.get($window.SC, 'processors.CC.name', null);
      var paymentMethod = processorName === 'STRIPE' ? 'Stripe' : 'CC';

      // set default to cc, also calculates fees
      this.setFeeParams({ paymentMethod: paymentMethod, processorName: processorName, isAmex: false, isInternational: false });

      // Destroy on expire
      this.on('expire', function () {
        return _this.destroy();
      });

      $rootScope.$on('fixedFotPercent:updated', function () {
        _this.calculateFees({ donation: true, registration: true });
      });

      $rootScope.$on('feesCalculated', function (_event, type) {
        _this.setGrossAdjustmentParams(type);
      });
    }

    _createClass(CartModel, [{
      key: 'donationFeeOnTop',
      value: function donationFeeOnTop() {
        return this.current.donation_fee_on_top || this.current.donation_donor_covered_fee;
      }

      /* ---------------------------------------------------------------------- *
       * populateFrMinimumCommitments()
       *
       * Helper for syncModel to repopulate the commitment data for the model when
       * it is wiped due to reserve and refetch. Not intended for public use.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'populateFrMinimumCommitments',
      value: function populateFrMinimumCommitments(data) {
        // May want to fetch the latest ticketTypes vs using what's in the in-memory cache
        var ticketTypesLookup = {};
        var ticketTypes = _.get(scCampaignsService, 'active.current.ticketTypes');
        if (!ticketTypes || !ticketTypes.length || !data.items || !data.items.length) {
          return;
        }
        ticketTypes.forEach(function (t) {
          ticketTypesLookup[t.id] = t;
        });
        data.items.forEach(function (i) {
          i.commitment = ticketTypesLookup[i.product_id].commitment;
        });
      }

      /* ---------------------------------------------------------------------- *
       * syncModel()
       *
       * Given a data object, sets up the model and stores its ID in a cookie.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'syncModel',
      value: function syncModel(data) {
        if (!_.get(data, 'items[0].commitment')) {
          this.populateFrMinimumCommitments(data);
        }

        // We should not be storing the capabilities value that comes back from the the api
        // as this is not a field that a non-admin can save to a cart. We still allow admins
        // to set this field when reserving a cart offline, however. See reserve().
        delete data.capabilities;

        this.current = _.cloneDeep(data);
        this.saved = _.cloneDeep(data);

        this.promoCode = data.promo_code || {};

        if (this.isReserved) {
          this.setTimers();
          this.store();
        }

        return this;
      }

      /* ---------------------------------------------------------------------- *
       * store()
       *
       * Stores the cart ID and metadata in a cookie.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'store',
      value: function store() {
        var cookieName = SC_CART_COOKIE + '_' + this.current.campaign_id;
        var cartData = {
          id: this.current.id,
          metadata: this.metadata
        };

        // Preserve user's last viewed state
        if (ipCookie(cookieName)) {
          cartData = _.merge(ipCookie(cookieName), {
            id: this.current.id,
            metadata: this.metadata
          });
        }

        ipCookie(cookieName, cartData, {
          expires: this.timeRemaining,
          expirationUnit: 'milliseconds',
          path: '/'
        });
      }

      /* ---------------------------------------------------------------------- *
       * refresh()
       *
       * Refreshes the cart timer and sets up the model with fresh data.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'refresh',
      value: function refresh() {
        var _this2 = this;

        return $http.post('/frs-api/carts/' + this.current.id + '/reset-timer').then(function (response) {
          return response.data;
        }).then(function (cart) {
          return _this2.syncModel(cart);
        });
      }

      /* ---------------------------------------------------------------------- *
       * reserve()
       *
       * Creates the cart model in the API and adds staged items to it. Up
       * until this point, we've been operating with a front-end-only instance.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'reserve',
      value: function reserve(offline) {
        var _this3 = this;

        if (offline) {
          this.current.capabilities = ['buy-unavailable-tickets'];
          this.current.registration_applied_fot_percent = null;
        }

        var createData = _.pick(this.current, EDITABLE_PROPS);

        // if a team_id exists on the cart, we are attempting to join a team.
        // that means we should not be setting oter team properties.
        if (createData.team_id) {
          createData.team_name = null;
        }

        return this.createCart(createData).then(function (response) {
          return _this3.syncModel(response.data);
        }) // Sets ID for updateItems
        .then(function () {
          return _this3.updateItems(_this3.stagedItems);
        }).then(function (response) {
          return _this3.syncModel(response.data);
        }).catch(function (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        });
      }

      /* ---------------------------------------------------------------------- *
       * setTimers
       *
       * Invokes registered callbacks when time remaining reaches the warning
       * threshold and runs out.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'setTimers',
      value: function setTimers() {
        var _this4 = this;

        $timeout.cancel(this.warnTimer);
        $timeout.cancel(this.expireTimer);
        this.warnTimer = $timeout(function () {
          return _this4.emit('warn');
        }, Math.max(this.timeRemaining - WARN_AT, 0));
        this.expireTimer = $timeout(function () {
          return _this4.emit('expire');
        }, Math.max(this.timeRemaining, 0));
      }

      /* ---------------------------------------------------------------------- *
       * stageItem()
       *
       * Adds an item to the stagedItems array. These will be converted into
       * cartItems when reserve() is called. We also update the
       * this.attendeeIndices model which holds the indexing info for list UI.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'stageItem',
      value: function stageItem(product) {
        var lastIdx = this.attendeeIndices.length - 1;
        var totalEntries = this.attendeeIndices[lastIdx] || 0;
        var productAttendees = product.entries_per_ticket || product.registrants.length;
        this.attendeeIndices.push(totalEntries + productAttendees);

        var itemToStage = {
          product_id: product.id,
          price: product.price
        };

        if (product.commitment) {
          itemToStage.commitment = product.commitment;
        }
        this.stagedItems.push(itemToStage);

        var currency = scCurrencyService.getEntityCampaignCurrency(scCampaignsService.active.current);
        scAnalytics.eventBeacon({
          category: 'frs ' + scCampaignsService.active.current.type,
          action: 'add',
          label: 'Add to Cart',
          product_name: product.name,
          price: product.price,
          ticket_id: product.id,
          currency: currency
        });

        // Set registrants if we're staging a multiple-attendee reg.
        var entries = _.get(product, 'entries_per_ticket', false);
        if (entries) {
          var stagedRegistration = this.stagedItems[this.stagedItems.length - 1];
          stagedRegistration.registrants = [];
          _.times(entries, function (i) {
            stagedRegistration.registrants.push({
              first_name: 'Attendee ' + (i + 1),
              last_name: '',
              email_address: '',
              answers: []
            });
          });
        }

        this.calculateFees({ registration: true });
      }

      /* ---------------------------------------------------------------------- *
       * unstageItem()
       *
       * Removes an item from the stagedItems array. Since there can be
       * multiple staged items of the same product, pass in the index
       * of the product that we are removing if the user is removing items
       * in a specific place in the list from the registration cart list.
       * If no index is passed in, that means it doesn't matter which matching
       * product id is removed (like if you are using the (-) on a ticket).
       * ---------------------------------------------------------------------- */

    }, {
      key: 'unstageItem',
      value: function unstageItem(product) {
        var _this5 = this;

        var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

        var numRegistrants = product.entries_per_ticket || product.entries || product.registrants.length;

        var removalIdx = index !== null ? index : _.findLastIndex(this.stagedItems, function (i) {
          return i.product_id == product.id;
        });

        // Remove item from cart
        this.stagedItems.splice(removalIdx, 1);

        // Update/shift the indices for all cart items' attendees
        this.attendeeIndices.splice(removalIdx, 1);
        this.attendeeIndices.forEach(function (currValue, idx) {
          _this5.attendeeIndices[idx] = idx >= removalIdx ? currValue - numRegistrants : currValue;
        });

        var currency = scCurrencyService.getEntityCampaignCurrency(scCampaignsService.active.current);
        scAnalytics.eventBeacon({
          category: 'frs ' + scCampaignsService.active.current.type,
          action: 'remove',
          label: 'Remove from Cart',
          price: product.price,
          product_name: product.name,
          ticket_id: product.id,
          currency: currency
        });

        if (!this.stagedItems.length) {
          this.attendeeIndices = [];
        }

        this.calculateFees({ registration: true });
      }

      /* ---------------------------------------------------------------------- *
       * unstageItemById()
       *
       * Removes all the staged items that coorespond with the productId. This
       * method can only be used on an unreserved cart.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'unstageItemById',
      value: function unstageItemById(productId) {
        var _this6 = this;

        this.stagedItems = this.stagedItems.filter(function (item, index) {
          if (item.product_id === productId) {
            _this6.attendeeIndices.splice(index, 1);
            return false;
          }
          return true;
        });
      }

      /* ---------------------------------------------------------------------- *
       * getRegistrant()
       *
       * Returns a copy of the registrant in the provided position, or an
       * empty object.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'getRegistrant',
      value: function getRegistrant(cartItemId, index) {
        var cartItem = _.find(this.current.items, { id: cartItemId });
        return _.get(cartItem, ['registrants', index], {
          cart_item_id: cartItemId,
          first_name: '',
          last_name: '',
          email_address: '',
          answers: []
        });
      }

      /* ---------------------------------------------------------------------- *
       * setRegistrant()
       *
       * Updates the registrant in the provided position if it exists, or else
       * creates the registrant record.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'setRegistrant',
      value: function setRegistrant(cartItemId, index, registrant) {
        var _this7 = this;

        // set from staging to current, then update
        var cartItem = _.find(this.current.items, { id: cartItemId });
        var stagedItem = _.find(this.stagedItems, { id: cartItemId });
        var currRegistrant = _.get(cartItem, ['registrants', index]);
        if (!_.isEmpty(registrant.answers)) {
          registrant.answers = registrant.answers.filter(function (answer) {
            return answer.answer !== '' && !_.isNull(answer.answer) && answer.answer !== 'Please select';
          });
        }

        if (currRegistrant && !_.isEqual(registrant, currRegistrant)) {
          return this.updateRegistrant(stagedItem.registrants[index].id, registrant).then(function () {
            return _this7.refresh();
          });
        } else if (!currRegistrant) {
          return this.createRegistrant(stagedItem.id, registrant).then(function (response) {
            stagedItem.registrants[index].id = response.data.id;
            stagedItem.registrants[index].registration_id = null;

            // Return the updated cart with the new attendee saved,
            // this is needed in regNav's setLastState
            return _this7.refresh();
          });
        }

        return this.refresh();
      }

      /* ---------------------------------------------------------------------- *
       * getCurrentRegistrantDonationCreditee()
       *
       * Returns the registrant from both the current cart that is to be credited
       * with a donation or null if no registrant or creditee
       * ---------------------------------------------------------------------- */

    }, {
      key: 'getCurrentRegistrantDonationCreditee',
      value: function getCurrentRegistrantDonationCreditee() {
        if (!this.current.items) {
          return null;
        }

        var donationCrediteeCartItem = this.current.items.find(function (i) {
          return i.registrants && i.registrants.some(function (r) {
            return r.donation_share;
          });
        });

        return donationCrediteeCartItem ? donationCrediteeCartItem.registrants.find(function (r) {
          return r.donation_share;
        }) : null;
      }

      /* ---------------------------------------------------------------------- *
       * quantityOf()
       *
       * Returns the quantity of a given product in the stagedItems array.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'quantityOf',
      value: function quantityOf(product) {
        var collection = this.isReserved ? this.current.items : this.stagedItems;
        return _.filter(collection, { product_id: product.id }).length;
      }

      /**
       * itemsWithQuestions()
       *
       * Returns cart items that have custom questions that must be asked of registrants
       */

    }, {
      key: 'itemsWithQuestions',
      value: function itemsWithQuestions(items) {
        var _this8 = this;

        return items.filter(function (item) {
          return _this8.itemHasQuestions(item);
        });
      }

      /**
       * itemHasQuestions
       *
       * Return true if the cart item has custom questions, and false if it doesn't
       * Note: null product_id means that it's available for all tickets
       */

    }, {
      key: 'itemHasQuestions',
      value: function itemHasQuestions(_ref) {
        var productId = _ref.product_id;

        var questions = scCampaignsService.active.current.questions.filter(function (_ref2) {
          var location = _ref2.location,
              isDeleted = _ref2.is_deleted;
          return location === 'campaign_registration' && isDeleted === false;
        });
        // case product_id match: the question is valid for the specific product_id of this item
        // case product_id null: the question is valid for all the items in the campaign
        // case product_id 0: this is a recently saved question meant to be product_id null,
        //                    there can't be a ticket_id = 0 anyway so this is fine.
        return questions.some(function (question) {
          return [0, null, productId].includes(question.product_id);
        });
      }

      /**
       * nextItemWithQuestions
       *
       * This function finds the next item with questions.
       *
       * Pass this function an array of cart items and the current cart item.
       */

    }, {
      key: 'nextItemWithQuestions',
      value: function nextItemWithQuestions() {
        var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
        var _ref3 = arguments[1];
        var id = _ref3.id;

        var itemIndex = items.findIndex(function (item) {
          return item.id === id;
        });
        return items.slice(itemIndex + 1).find(this.itemHasQuestions);
      }

      /**
       * previousItemWithQuestions
       *
       * Basically the same as the function above, but this one returns the previous cart item with
       * questions, or false if there are none.
       *
       * Pass this function an array of cart items and the current cart item.
       */

    }, {
      key: 'previousItemWithQuestions',
      value: function previousItemWithQuestions() {
        var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
        var _ref4 = arguments[1];
        var id = _ref4.id;

        var itemIndex = items.findIndex(function (item) {
          return item.id === id;
        });
        return items.slice(0, itemIndex).filter(this.itemHasQuestions).pop();
      }

      /**
       * firstItemWithQuestionsIdx
       *
       * Returns the index of the first item that has a question from a given items array,
       * -1 if not existant
       */

    }, {
      key: 'firstItemWithQuestionsIdx',
      value: function firstItemWithQuestionsIdx() {
        var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

        var questionItems = this.itemsWithQuestions(items);
        return items.indexOf(questionItems[0]);
      }

      /**
       * lastItemWithQuestionsIdx
       *
       * Returns the index of the last item that has a question from a given items array,
       * -1 if not existant
       */

    }, {
      key: 'lastItemWithQuestionsIdx',
      value: function lastItemWithQuestionsIdx() {
        var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

        var questionItems = this.itemsWithQuestions(items);
        if (questionItems.length === 0) {
          return -1;
        }
        return items.indexOf(questionItems[questionItems.length - 1]);
      }

      /* ---------------------------------------------------------------------- *
       * setPurchaserInformation()
       *
       * Updates the purchaser_information prop if it exists, and creates it
       * if it doesn't
       * ---------------------------------------------------------------------- */

    }, {
      key: 'setPurchaserInformation',
      value: function setPurchaserInformation(purchaser) {
        this.purchaser_information = purchaser;

        return this.purchaser_information;
      }

      /* ---------------------------------------------------------------------- *
       * removeItem()
       *
       * Remove an item from the items array.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'removeItem',
      value: function removeItem(item) {
        var _this9 = this;

        _.remove(this.current.items, { id: item.id });
        this.calculateFees({ registration: true });
        return this.deleteItem(item.id).then(function () {
          return _this9.refresh();
        });
      }

      /* ---------------------------------------------------------------------- *
       * saveData()
       *
       * Persist changes to editable props. Allow callback to be passed in,
       * in case we want to execute something else before refreshing and syncing
       * the cart (for example, crediting the additional donation to a
       * registrant).
       * ---------------------------------------------------------------------- */

    }, {
      key: 'saveData',
      value: function saveData(data) {
        var _this10 = this;

        var cb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : $q.resolve();

        // Always update this.current with whatever data we're passing in
        _.merge(this.current, data);

        // update the current cart with the current values of editable properties and any
        // data we're trying to send up
        // We merge the editable props with the data in case someone wants to send up data which is
        // not part of the editable props
        return updateCart(this.current.id, _.merge(_.pick(this.current, EDITABLE_PROPS),
        // the code which handles deleting donations also for some reason stores the
        // donation_frequency back to the cart after a donation is removed. The API will throw
        // errors if we include the donation_frequency field without the other donation fields,
        // so make a really quick attempt to filter out the donation_frequency field from the
        // request if we would be sending it with an undefined donation_amount
        // Adding default values to these (i.e. 'one-time') for when we save without a donation
        // and then later add one we need a default value here. This is a hacky temp solution --
        // we need to figure out a better way to reset these values
        {
          donation_frequency: this.current.donation_amount === null ? null : this.current.donation_frequency,
          donation_fee_on_top: this.current.donation_amount === null ? null : this.current.donation_fee_on_top,
          donation_currency_code: this.current.donation_amount === null ? null : this.current.donation_currency_code
        }, data)).then(cb).then(function () {
          return _this10.refresh();
        });
      }

      // For when you just want to update the server with the full contents of the cart

    }, {
      key: 'saveCart',
      value: function saveCart() {
        return updateCart(this.current.id, this.current);
      }

      /* ---------------------------------------------------------------------- *
       * removeItem()
       *
       * Remove an item from the items array.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'deleteItem',
      value: function deleteItem(itemId) {
        return $http.delete('/frs-api/cart-items/' + itemId);
      }

      /* ---------------------------------------------------------------------- *
       * createRegistrant()
       *
       * Persist a new registrant record. Internal use only.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'createRegistrant',
      value: function createRegistrant(cartItemId, registrant) {
        return postWithTimeout('/frs-api/cart-items/' + cartItemId + '/cart-registrants', registrant);
      }

      /* ---------------------------------------------------------------------- *
       * deleteRegistrant()
       *
       * Persist the removal of a registrant record. Internal use only.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'deleteRegistrant',
      value: function deleteRegistrant(cartRegistrantId) {
        return $http.delete('/frs-api/cart-registrants/' + cartRegistrantId);
      }

      /* ---------------------------------------------------------------------- *
       * updateRegistrant()
       *
       * Persist a new registrant record. Internal use only.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'updateRegistrant',
      value: function updateRegistrant(cartRegistrantId, registrant) {
        return putWithTimeout('/frs-api/cart-registrants/' + cartRegistrantId, registrant);
      }

      /* ---------------------------------------------------------------------- *
       * createCart()
       *
       * Persist a new cart record. Internal use only.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'createCart',
      value: function createCart(data) {
        return postWithTimeout('/frs-api/campaigns/' + this.current.campaign_id + '/carts', data);
      }

      /* ---------------------------------------------------------------------- *
       * deleteCart()
       *
       * Delete a new cart record. Internal use only.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'deleteCart',
      value: function deleteCart() {
        return $http.delete('/frs-api/carts/' + this.current.id);
      }

      /* ---------------------------------------------------------------------- *
       * updateItems()
       *
       * Persist changes to cart items collection. Internal use only.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'updateItems',
      value: function updateItems(data) {
        return putWithTimeout('/frs-api/carts/' + this.current.id + '/cart-items', data);
      }

      /* ---------------------------------------------------------------------- *
       * watchDonation()
       *
       * Watch donation properties and, if needed, recalculate donation fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'watchDonation',
      value: function watchDonation() {
        var _this11 = this;

        this.deregisterWatchDonation = $rootScope.$watchGroup([function () {
          return _this11.current.donation_fee_on_top;
        }, function () {
          return _this11.current.donation_donor_covered_fee;
        }, function () {
          return _this11.current.donation_amount;
        }, function () {
          return _this11.isInternational;
        }, function () {
          return _this11.isAmex;
        }], function () {
          return _this11.calculateFees({ donation: true });
        });
      }

      /* ---------------------------------------------------------------------- *
       * getItemPrice()
       *
       * Check item price minus the discount amount
       * ---------------------------------------------------------------------- */

    }, {
      key: 'getFinalPrice',
      value: function getFinalPrice(item) {
        var itemPrice = parseFloat(item.price) - (parseFloat(item.adjustment_amount) || 0) < 0 ? 0 : parseFloat(item.price) - (parseFloat(item.adjustment_amount) || 0);
        return itemPrice;
      }

      /* ---------------------------------------------------------------------- *
       * setFeeParams()
       *
       * Fee Params to be used for completing the cart
       * transaction, used for fee calculation. Done together to avoid multiple
       * recalculation of fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'setFeeParams',
      value: function setFeeParams(_ref5) {
        var paymentMethod = _ref5.paymentMethod,
            processorName = _ref5.processorName,
            isAmex = _ref5.isAmex,
            isInternational = _ref5.isInternational;

        var recaluateFees = false;

        if (paymentMethod && this.paymentMethod !== paymentMethod) {
          this.paymentMethod = paymentMethod;
          scFeeOnTopService.setPaymentMethod(this.paymentMethod);
          recaluateFees = true;
        }

        if (processorName && this.processorName !== processorName) {
          this.processorName = processorName;
          scFeeOnTopService.setProcessorName(this.processorName);
          recaluateFees = true;
        }

        if (isAmex !== undefined && this.isAmex !== isAmex) {
          this.isAmex = !!isAmex;
          scFeeOnTopService.setAmex(this.isAmex);
          recaluateFees = true;
        }

        if (isInternational !== undefined && this.isInternational !== isInternational) {
          this.isInternational = !!isInternational;
          scFeeOnTopService.setInternational(this.isInternational);
          recaluateFees = true;
        }

        if (recaluateFees) {
          this.calculateFees({ donation: true, registration: true });
        }
      }

      /* ---------------------------------------------------------------------- *
       * on()
       *
       * Store event handlers.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'on',
      value: function on(event, callback) {
        this.callbacks[event].push(callback);
      }

      /* ---------------------------------------------------------------------- *
       * emit()
       *
       * Fire event handlers, then broadcast the event as an Angular event.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'emit',
      value: function emit(event) {
        var _this12 = this;

        _.forEach(this.callbacks[event], function (fn) {
          return fn(_this12);
        });
        $rootScope.$broadcast('scCartModel.' + event, this);
      }

      /* ---------------------------------------------------------------------- *
       * destroy()
       *
       * Remove watches, immediately delete reserved items, clear cookie.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'destroy',
      value: function destroy() {
        this.deregisterWatchDonation();
        $timeout.cancel(this.warnTimer);
        $timeout.cancel(this.expireTimer);
        var cookieName = SC_CART_COOKIE + '_' + this.current.campaign_id;
        ipCookie(cookieName, null, {
          expires: 0,
          expirationUnit: 'milliseconds',
          path: '/'
        });
        if (!this.isComplete && !this.isErrored && this.timeRemaining && this.current.id) {
          return this.deleteCart();
        }
        return $q.resolve();
      }

      /* ---------------------------------------------------------------------- *
       * hasMultipleTransactions
       *
       * Returns true if there are both donation and registration transactions
       * to be charged. Used for setting paypal as an eligible payment method
       * ---------------------------------------------------------------------- */

    }, {
      key: 'hasMultipleTransactions',
      value: function hasMultipleTransactions() {
        return this.itemsDiscountedSubtotal > 0 && this.current.donation_amount > 0;
      }

      /* ---------------------------------------------------------------------- *
       * timeRemaining
       *
       * Returns the time in ms until the cart expires.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'submitOffline',


      /* ---------------------------------------------------------------------- *
       * submitOffline
       *
       * Submit the offline cart
       * ---------------------------------------------------------------------- */

      value: function submitOffline() {
        return $http({
          method: 'POST',
          url: '/frs-api/carts/' + this.current.id + '/confirm-offline',
          data: this.current
        });
      }

      /* ---------------------------------------------------------------------- *
       * submit
       *
       * Submit the cart.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'submit',
      value: function () {
        var _ref6 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(payment) {
          var _this13 = this;

          return regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  scBanner.close();

                  payment.dcfEnabled = scFeeOnTopService.donorCoveredFeesEnabled;

                  payment.donationFeeSum = scFeeOnTopService.getFeesByType('donation');
                  payment.registrationFeeSum = scFeeOnTopService.getFeesByType('registration');

                  // Checking for applied_fot_percent in case the cart has already been
                  // confirmed and we sent the flag that we're using fixed fot. Zero is
                  // a valid fot percent so checking for null and undefined
                  payment.fixedFotEnabled = scFeeOnTopService.fixedFotEnabled || !_.isNull(this.current.applied_fot_percent) && !_.isUndefined(this.current.applied_fot_percent);

                  payment.fixedFotPercent = scFeeOnTopService.fixedFotPercent;

                  this.current.registrant_opted_into_payment_sync = payment.registrant_opted_into_payment_sync;

                  // APIv2 does not allow carts to be updated if they have already been submitted.
                  // However, the payment method can still be updated if Pay returned 402 earlier.

                  if (this.metadata.partialPaymentSuccess) {
                    _context.next = 10;
                    break;
                  }

                  _context.next = 10;
                  return this.saveCart();

                case 10:
                  return _context.abrupt('return', recaptchaService.createToken('DONATE').then(function (recaptchaToken) {
                    // eslint-disable-next-line prefer-promise-reject-errors
                    if (!recaptchaToken) return Promise.reject('RECAPTCHA');

                    return $http.post('/frs-api/carts/' + _this13.current.id + '/confirm', {
                      payment: payment,
                      metadata: _this13.metadata,
                      token: recaptchaToken
                    });
                  }).then(function (result) {
                    if (result.data.status === 202) {
                      scBanner(result.data.message, 'success', false);
                    }

                    _this13.metadata.status = 'complete';
                    _this13.metadata.verify = result.data.verify;

                    $http.get('/frs-api/carts/' + _this13.current.id + '/receipt-totals?verify=' + _this13.metadata.verify).then(function (response) {
                      var receiptData = response.data;
                      var conversionData = _.merge(_this13.current, {
                        amount: receiptData.transaction_total,
                        raw_currency_code: scCurrencyService.getEntityCampaignCurrency(scCampaignsService.active.current),
                        promo_code_code: _.get(_this13.current, 'promo_code.code', null),
                        registration_transaction_id: receiptData.registration_transaction_id,
                        member_id: receiptData.registration_member_id
                      });
                      scAnalytics.track('transaction', { transaction: conversionData });
                      conversionData = _.merge(conversionData, {
                        campaignName: scCampaignsService.active.current.name,
                        country: scCampaignsService.active.current.country,
                        designationId: scCampaignsService.active.current.designation_id,
                        frequency: receiptData.donation_frequency,
                        promoCode: _.get(_this13.current, 'promo_code.code', null)
                      });
                      scAnalytics.track('registration/create/complete', conversionData);
                    }).catch(function () {});
                    _this13.store();
                  }).catch(function (err) {
                    // if the error response comes back with a fixed_fot_percent attached
                    // this means that the org has changed the percentage while the donor
                    // was in the middle of checkout
                    if (err.data && err.data.fixed_fot_percent) {
                      return _this13.handleFixedFotUpdate(err.data.fixed_fot_percent);
                    }

                    var message = err.data || "Sorry, you've encountered an error. Please contact <a target='_blank' href='https://prosupport.gofundme.com/hc/en-us'>customer support.</a>";
                    switch (err.status) {
                      case 500:
                        _this13.metadata.status = 'error';
                        scBanner(message, 'error', true);
                        bugsnagClient.notify('Error submitting payment, status: ' + err.status + ', message: ' + message);
                        break;
                      case 402:
                        {
                          // We will only ever be allowing retries if there was a 402 from the payment backend
                          _this13.metadata.status = 'error';
                          scBanner(message, 'error', true);
                          var canRetryDonation = !!ipCookie('cart.' + _this13.current.id + '.checkout.retry_nonce.donation');
                          var canRetryRegistration = !!ipCookie('cart.' + _this13.current.id + '.checkout.retry_nonce.registration');
                          _this13.metadata.partialPaymentSuccess = canRetryRegistration || canRetryDonation;
                          break;
                        }
                      default:
                        _this13.metadata.status = 'error';
                        bugsnagClient.notify('Error submitting payment, status: ' + err.status + ', message: ' + message);
                    }
                    _this13.store();
                    return $q.reject();
                  }));

                case 11:
                case 'end':
                  return _context.stop();
              }
            }
          }, _callee, this);
        }));

        function submit(_x10) {
          return _ref6.apply(this, arguments);
        }

        return submit;
      }()

      /* ------------------------------------------------------------------ *
       * Private Helpers
       * ------------------------------------------------------------------ */

    }, {
      key: 'calculateFees',
      value: function calculateFees(_ref7) {
        var _ref7$donation = _ref7.donation,
            donation = _ref7$donation === undefined ? false : _ref7$donation,
            _ref7$registration = _ref7.registration,
            registration = _ref7$registration === undefined ? false : _ref7$registration;

        var cartItems = this.current.items || this.stagedItems;

        if (donation) {
          scFeeOnTopService.calculateFees({
            type: 'donation',
            fot: this.donationFeeOnTop(),
            amount: this.current.donation_amount,
            frequency: this.current.donation_frequency
          });

          if (scFeeOnTopService.donorCoveredFeesEnabled) {
            this.current.donation_applied_fot_percent = null;
          }

          if (scFeeOnTopService.fixedFotEnabled) {
            this.current.donation_applied_fot_percent = this.current.donation_fee_on_top ? parseFloat(scCampaignsService.active.current.effective_fixed_fot_percent) : null;
          }
        }

        if (registration) {
          scFeeOnTopService.calculateFees({
            cartItems: cartItems,
            type: 'registration',
            fot: this.registrationFeeOnTop,
            amount: this.itemsDiscountedSubtotal,
            frequency: this.current.donation_frequency
          });

          if (scFeeOnTopService.donorCoveredFeesEnabled) {
            this.current.registration_applied_fot_percent = null;
          }

          if (scFeeOnTopService.fixedFotEnabled) {
            this.current.registration_applied_fot_percent = scCampaignsService.active.current.ticket_pass_on_fees ? parseFloat(scCampaignsService.active.current.effective_fixed_fot_percent) : null;
          }
        }
      }
    }, {
      key: 'handleFixedFotUpdate',
      value: function handleFixedFotUpdate(newFixedFotPercent) {
        scFeeOnTopService.updateFixedFotPercent(newFixedFotPercent);
        var msg = 'Oops. The organization\'s fees have changed to ' + newFixedFotPercent + '%. See new calulated total below.';
        scBanner(msg, 'neutral', true);
        this.metadata.status = 'error';
        var canRetryDonation = !!ipCookie('cart.' + this.current.id + '.checkout.retry_nonce.donation');
        var canRetryRegistration = !!ipCookie('cart.' + this.current.id + '.checkout.retry_nonce.registration');
        this.metadata.partialPaymentSuccess = canRetryRegistration || canRetryDonation;
        this.store();
        return $q.reject();
      }
    }, {
      key: 'setGrossAdjustmentParams',
      value: function setGrossAdjustmentParams(type) {
        if (!scFeeOnTopService.donorCoveredFeesEnabled) {
          return;
        }

        var grossAdjustment = scFeeOnTopService.fees[type].grossAdjustment;

        this.current[type + '_application_fee_percent'] = _.get(grossAdjustment, 'applied_application_fee_percent', null);

        this.current[type + '_processor_fee_percent'] = _.get(grossAdjustment, 'applied_processor_fee_percent', null);

        this.current[type + '_raw_processor_fee_flat'] = _.get(grossAdjustment, 'applied_raw_processor_fee_flat', null);

        this.current[type + '_flex_rate_percent'] = _.get(grossAdjustment, 'applied_flex_rate_percent', null);

        this.current[type + '_raw_application_fee_amount'] = _.get(grossAdjustment, 'raw_application_fee_amount', null);

        this.current[type + '_raw_processor_fee_amount'] = _.get(grossAdjustment, 'raw_processor_fee_amount', null);

        this.current[type + '_raw_flex_rate_amount'] = _.get(grossAdjustment, 'raw_flex_rate_amount', null);
      }

      /* ---------------------------------------------------------------------- *
       * _now()
       *
       * Testing only.
       * ---------------------------------------------------------------------- */

    }, {
      key: '_now',
      value: function _now() {
        // eslint-disable-line no-underscore-dangle
        return moment().valueOf();
      }
    }, {
      key: 'timeRemaining',
      get: function get() {
        var now = this._now(); // eslint-disable-line no-underscore-dangle
        var remaining = moment(this.current.expires_at) - now;
        return Math.max(remaining, 0);
      }

      /* ---------------------------------------------------------------------- *
       * isPristine
       *
       * Returns true if this cart has not been reserved and has no staged
       * items or team data.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'isPristine',
      get: function get() {
        return !this.isReserved && !this.stagedItems.length && !this.current.team_id && !this.current.team_name;
      }

      /* ---------------------------------------------------------------------- *
       * isReserved
       *
       * Returns true if this cart has been saved in the API.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'isReserved',
      get: function get() {
        return !!(this.current.id && _.get(this.current, 'items.length'));
      }

      /* ---------------------------------------------------------------------- *
       * isComplete
       *
       * Returns true if this cart has been successfully submitted to the API.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'isComplete',
      get: function get() {
        return _.get(this, 'metadata.status') === 'complete';
      }

      /* ---------------------------------------------------------------------- *
       * isErrored
       *
       * Returns true if this cart has been submitted once but failed due to
       * payment info or another reason. In this situation the cart can no
       * longer be updated.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'isErrored',
      get: function get() {
        return _.get(this, 'metadata.status') === 'error';
      }

      /* ---------------------------------------------------------------------- *
       * isCalculating
       *
       * Returns true if fees are calculating for the cart.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'isCalculating',
      get: function get() {
        return scFeeOnTopService.isCalculating();
      }

      /* ---------------------------------------------------------------------- *
       * itemsSubtotal
       *
       * Returns the price of all staged or committed items, minus fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'itemsSubtotal',
      get: function get() {
        var items = this.isReserved ? this.current.items : this.stagedItems;
        return _.reduce(items, function (sum, item) {
          return sum + parseFloat(item.price);
        }, 0);
      }

      /* ---------------------------------------------------------------------- *
       * itemsSubtotal
       *
       * Returns the price of all staged or current items as well as donation,
       * minus fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'subtotal',
      get: function get() {
        return this.itemsSubtotal + parseFloat(this.current.donation_amount || 0);
      }

      /* ---------------------------------------------------------------------- *
       * itemsDiscountedSubtotal
       *
       * Returns the discounted price of all staged or committed items, minus fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'itemsDiscountedSubtotal',
      get: function get() {
        var items = this.isReserved ? this.current.items : this.stagedItems;
        return items.map(function (_ref8) {
          var price = _ref8.price,
              adjustment_amount = _ref8.adjustment_amount;

          if (parseFloat(price) - (parseFloat(adjustment_amount) || 0) < 0) {
            return 0;
          }
          return parseFloat(price) - (parseFloat(adjustment_amount) || 0);
        }).map(function (amount) {
          return Math.max(0, amount);
        }).reduce(function (sum, value) {
          return sum + value;
        }, 0);
      }

      /* ---------------------------------------------------------------------- *
       * discountedSubtotal
       *
       * Returns the discounted price of all staged or committed items, minus fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'discountedSubtotal',
      get: function get() {
        return this.itemsDiscountedSubtotal + parseFloat(this.current.donation_amount || 0);
      }

      /* ---------------------------------------------------------------------- *
       * total
       *
       * Returns the total price for all current cart items, with fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'total',
      get: function get() {
        return Math.max(this.discountedSubtotal + scFeeOnTopService.totalFees(), 0);
      }

      /* ---------------------------------------------------------------------- *
       * fee getters
       *
       * Return the various fees.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'totalFees',
      get: function get() {
        return scFeeOnTopService.totalFees();
      }
    }, {
      key: 'registrationFees',
      get: function get() {
        return scFeeOnTopService.getFeesByType('registration');
      }
    }, {
      key: 'donationFees',
      get: function get() {
        return scFeeOnTopService.getFeesByType('donation');
      }

      /* ---------------------------------------------------------------------- *
       * discountedAmount
       *
       * Returns the amount that is taken off the total when you apply a promo code
       * ---------------------------------------------------------------------- */

    }, {
      key: 'discountedAmount',
      get: function get() {
        var items = this.isReserved ? this.current.items : this.stagedItems;
        var discountAmounts = items
        // Filters out falsey values -- this disregards items with no applicable discount
        .filter(function (_ref9) {
          var adjustmentAmount = _ref9.adjustment_amount;
          return adjustmentAmount;
        })
        // If the raw discounted amount is negative, set the total discount to total price
        // of discounted items. This ensures that the discount displayed is "true" discount
        // applied.
        .map(function (_ref10) {
          var price = _ref10.price,
              adjustmentAmount = _ref10.adjustment_amount;

          var parsedPrice = parseFloat(price);
          var parsedAdjustment = parseFloat(adjustmentAmount) || 0;
          if (parsedPrice < parsedAdjustment) {
            return parsedPrice;
          }
          return parsedAdjustment;
        });

        return _.sum(discountAmounts);
      }

      /* ---------------------------------------------------------------------- *
       * quantity
       *
       * Returns the total number of staged or committed items.
       * ---------------------------------------------------------------------- */

    }, {
      key: 'quantity',
      get: function get() {
        return this.isReserved ? this.current.items.length : this.stagedItems.length;
      }

      /* ---------------------------------------------------------------------- *
       * soloEntry()
       *
       * Returns whether this cart only has a single entry or not, used mostly
       * in order to determine whether the team captain screen is necessary
       * ---------------------------------------------------------------------- */

    }, {
      key: 'soloEntry',
      get: function get() {
        // there is no i in team but there is a meat in team and there is an i in meat pie
        return this.current.items.length === 1 && _.get(this.current, 'items[0].entries', 0) === 1;
      }
    }]);

    return CartModel;
  }();
}

angular.module('classy').factory('scCartModel', CartModelFactory);
})();